'use client';

import Link from 'next/link';
import { Product } from '@/types/database.types';

interface ProductCardProps {
  product: Product;
  onClick: (product: Product) => void;
}

interface AuthAwareProductCardProps extends ProductCardProps {
  isAuthenticated: boolean;
  authLoading: boolean;
}

const ProductImagePlaceholder = ({ svgPath }: { svgPath: string }) => (
  <div className="text-center p-6 flex flex-col items-center justify-center w-full h-full">
    <svg className="w-16 h-16 mx-auto mb-3 text-gray-400 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={svgPath} />
    </svg>
    <p className="text-sm text-gray-500 font-body text-center">Product image coming soon</p>
  </div>
);

const DEFAULT_SVG_PATH = "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z";

export const ProductCardSkeleton = () => (
  <div className="bg-white border-2 border-black overflow-hidden flex flex-col animate-pulse">
    <div className="h-3 bg-red-600" />
    <div className="h-64 bg-gray-100" />
    <div className="p-6 flex flex-col flex-grow gap-3">
      <div className="h-5 w-28 bg-gray-200 rounded" />
      <div className="h-4 w-full bg-gray-200 rounded" />
      <div className="h-4 w-3/4 bg-gray-200 rounded" />
      <div className="mt-auto h-11 w-full bg-gray-200 rounded" />
    </div>
  </div>
);

export const PublicProductCard = ({ product, onClick }: ProductCardProps) => (
  <div
    className="bg-white border-2 border-black overflow-hidden hover:shadow-xl transition-all duration-200 cursor-pointer flex flex-col group"
    onClick={() => onClick(product)}
  >
    {/* Red geometric accent bar */}
    <div className="h-1.5 bg-red-600 w-full" />

    {/* Product Image */}
    <div className="relative h-56 bg-gray-50 flex items-center justify-center overflow-hidden">
      {product.image_url ? (
        <>
          <img
            src={product.image_url}
            alt={product.image_alt || product.description || 'Product image'}
            className="object-contain p-4 w-full h-full group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const placeholder = e.currentTarget.nextElementSibling as HTMLElement | null;
              if (placeholder) placeholder.style.display = 'flex';
            }}
          />
          <div className="text-center p-6 flex flex-col items-center justify-center w-full h-full" style={{ display: 'none' }}>
            <svg className="w-16 h-16 mx-auto mb-3 text-gray-400 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={DEFAULT_SVG_PATH} />
            </svg>
            <p className="text-sm text-gray-500 font-body text-center">Product image coming soon</p>
          </div>
        </>
      ) : (
        <ProductImagePlaceholder svgPath={DEFAULT_SVG_PATH} />
      )}
    </div>

    {/* Product Details */}
    <div className="p-5 flex flex-col flex-grow border-t-2 border-black">
      {/* SKU Badge */}
      <div className="inline-flex items-center bg-black px-3 py-1 mb-3 self-start">
        <span className="font-mono text-xs font-bold text-white tracking-wider uppercase">SKU: {product.part_id}</span>
      </div>

      {/* Description */}
      <p className="font-body text-gray-700 text-sm mb-5 line-clamp-3 flex-grow leading-relaxed">{product.description}</p>

      {/* CTA */}
      <Link
        href="/customer-login"
        onClick={(e) => e.stopPropagation()}
        className="block w-full text-center px-6 py-3 bg-red-600 text-white font-bold text-sm tracking-wide border-2 border-red-600 transition-all duration-200 hover:bg-red-700 hover:border-red-700 hover:-translate-y-0.5 hover:shadow-md"
      >
        Login to View Pricing &amp; Order
      </Link>
    </div>
  </div>
);

export const GatedProductCard = ({ product, onClick }: ProductCardProps) => (
  <div
    className="bg-white border-2 border-black overflow-hidden hover:shadow-xl transition-all duration-200 cursor-pointer flex flex-col group"
    onClick={() => onClick(product)}
  >
    {/* Red geometric accent bar */}
    <div className="h-1.5 bg-red-600 w-full" />

    {/* Product Image */}
    <div className="relative h-56 bg-gray-50 flex items-center justify-center overflow-hidden">
      {product.image_url ? (
        <>
          <img
            src={product.image_url}
            alt={product.image_alt || product.description || 'Product image'}
            className="object-contain p-4 w-full h-full group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const placeholder = e.currentTarget.nextElementSibling as HTMLElement | null;
              if (placeholder) placeholder.style.display = 'flex';
            }}
          />
          <div className="text-center p-6 flex flex-col items-center justify-center w-full h-full" style={{ display: 'none' }}>
            <svg className="w-16 h-16 mx-auto mb-3 text-gray-400 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={DEFAULT_SVG_PATH} />
            </svg>
            <p className="text-sm text-gray-500 font-body text-center">Product image coming soon</p>
          </div>
        </>
      ) : (
        <ProductImagePlaceholder svgPath={DEFAULT_SVG_PATH} />
      )}
    </div>

    {/* Product Details */}
    <div className="p-5 flex flex-col flex-grow border-t-2 border-black">
      {/* SKU Badge */}
      <div className="inline-flex items-center bg-black px-3 py-1 mb-3 self-start">
        <span className="font-mono text-xs font-bold text-white tracking-wider uppercase">SKU: {product.part_id}</span>
      </div>

      {/* Description */}
      <p className="font-body text-gray-700 text-sm mb-4 line-clamp-3 flex-grow leading-relaxed">{product.description}</p>

      {/* Price block */}
      <div className="flex items-baseline justify-between mb-4 pb-4 border-b border-gray-200">
        <span className="font-heading text-3xl font-bold text-black">
          ${product.price?.toFixed(2) || '0.00'}
        </span>
        <span className="text-xs text-gray-500 font-body uppercase tracking-wide">per unit</span>
      </div>

      {/* Order Button */}
      <Link
        href="/submit-order"
        onClick={(e) => e.stopPropagation()}
        className="block w-full text-center px-6 py-3 bg-red-600 text-white font-bold text-sm tracking-wide border-2 border-red-600 transition-all duration-200 hover:bg-red-700 hover:border-red-700 hover:-translate-y-0.5 hover:shadow-md"
      >
        Add to Order
      </Link>
    </div>
  </div>
);

export const AuthAwareProductCard = ({ product, onClick, isAuthenticated, authLoading }: AuthAwareProductCardProps) => {
  if (authLoading) {
    return <ProductCardSkeleton />;
  }
  if (isAuthenticated) {
    return <GatedProductCard product={product} onClick={onClick} />;
  }
  return <PublicProductCard product={product} onClick={onClick} />;
};
